<!--
  概览页（路由 /resourcemanagement/overview）
  - 左右分栏布局；每栏内的模块支持上下拖拽排序
  - 左右两列独立分组，禁止跨列拖动
  - 排序后通过接口持久化（与登录用户绑定）；初始化时还原上次保存的顺序
-->
<template>
  <div class="home-overview-container">
    <div class="jc-page-header">
      <h1>
        <i class="jc-icon-my-circle-bold"></i>{{ $t('text.overview') }}
        <small class="drag-tip">{{ $t('overview.layoutDragTip') }}</small>
      </h1>
    </div>

    <div class="main-container">
      <!-- 左侧：可拖拽 -->
      <div class="main-content">
        <!-- WelcomeHeader 固定置顶，不参与拖拽 -->
        <div class="draggable-block draggable-block--pinned">
          <welcome-header/>
        </div>
        <draggable
          v-if="layoutReady"
          v-model="leftOrderDraggable"
          :animation="200"
          :group="{ name: 'left-only', pull: false, put: false }"
          ghost-class="dragging-ghost"
          chosen-class="dragging-chosen"
          drag-class="sortable-drag-item"
          :force-fallback="true"
          :fallback-tolerance="3"
          @start="onDragStart"
          @end="onDragEnd">
          <div
            v-for="key in leftOrderDraggable"
            :key="key"
            class="draggable-block">
            <component :is="leftModuleMap[key]"/>
          </div>
        </draggable>
        <!-- 布局未就绪时使用骨架占位，避免新用户首次进入时出现重排闪烁 -->
        <div
          v-else
          class="layout-skeleton">
          <div
            v-for="i in leftSkeletonCount"
            :key="`left-sk-${i}`"
            class="skeleton-block skeleton-block--left"/>
        </div>
      </div>

      <!-- 右侧：可拖拽 -->
      <div class="side-content">
        <draggable
          v-if="layoutReady"
          v-model="rightOrder"
          :animation="200"
          :group="{ name: 'right-only', pull: false, put: false }"
          ghost-class="dragging-ghost"
          chosen-class="dragging-chosen"
          drag-class="sortable-drag-item"
          :force-fallback="true"
          :fallback-tolerance="3"
          @start="onDragStart"
          @end="onDragEnd">
          <div
            v-for="key in rightOrder"
            :key="key"
            class="draggable-block">
            <component
              :is="rightModuleMap[key].comp"
              v-bind="rightModuleMap[key].props || {}"/>
          </div>
        </draggable>
        <div
          v-else
          class="layout-skeleton">
          <div
            v-for="i in rightSkeletonCount"
            :key="`right-sk-${i}`"
            class="skeleton-block skeleton-block--right"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import WelcomeHeader from './components/WelcomeHeader.vue';
import ResourceZone from './components/ResourceZone.vue';
import OpsMonitor from './components/OpsMonitor.vue';
import ResourceMonitor from './components/ResourceMonitor.vue';
import NoticeList from './components/sidePanelComponents/NoticeList.vue';
import CustomerService from './components/sidePanelComponents/CustomerService.vue';
import WorkOrderList from './components/sidePanelComponents/WorkOrderList.vue';
import QuickActions from './components/sidePanelComponents/QuickActions.vue';

// 默认顺序（用于兜底/还原）
const DEFAULT_LEFT = ['welcomeHeader', 'resourceMonitor', 'resourceZone', 'opsMonitor', ];
const DEFAULT_RIGHT = ['customerService', 'announcement', 'networkFreezeCutover', 'quickActions', 'workOrderList', ];

// localStorage 中存储布局的 key 前缀（按 pageKey 区分）
const LAYOUT_STORAGE_KEY_PREFIX = 'overview_layout_';
const LAYOUT_PAGE_KEY = 'home';
const LAYOUT_STORAGE_KEY = LAYOUT_STORAGE_KEY_PREFIX + LAYOUT_PAGE_KEY;

/**
 * 判断 layout 是否合法：对象类型，且 left/right 至少有一个为数组
 * 抽为模块级纯函数，便于在 data() 中同步使用
 */
function isValidLayout(layout) {
  if (!layout || typeof layout !== 'object') return false;
  return Array.isArray(layout.left) || Array.isArray(layout.right);
}

/**
 * 同步读取 localStorage 中的布局数据
 * 在 data() 初始化阶段使用，确保首屏即按用户上次顺序渲染，避免后续重排抖动
 */
function readLocalLayoutSync() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValidLayout(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
}

/**
 * 合并保存顺序与默认顺序：保留有效项，补齐缺失项
 */
function mergeOrder(saved, defaultList) {
  const valid = (saved || []).filter(k => defaultList.includes(k));
  defaultList.forEach((k) => { if (!valid.includes(k)) valid.push(k); });
  return valid;
}

/**
 * 强制将 welcomeHeader 固定在 left 数组首位
 * 无论历史数据（localStorage / DB）中位置如何，均自动修正
 */
function pinWelcomeHeaderFirst(order) {
  const PINNED = 'welcomeHeader';
  const rest = order.filter(k => k !== PINNED);
  return [PINNED, ...rest];
}

/**
 * 浅比较两个顺序数组是否完全一致
 */
function isSameOrder(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export default {
  name: 'HomeOverView',
  components: {
    draggable,
    WelcomeHeader,
    ResourceZone,
    OpsMonitor,
    ResourceMonitor,
    NoticeList,
    CustomerService,
    WorkOrderList,
    QuickActions
  },
  data() {
    // 同步读取 localStorage 中的布局，作为首屏初始顺序
    // 命中即首屏正确（覆盖老用户大多数场景），避免"先按默认顺序渲染→数据回来再重排"的视觉跳动
    const localLayout = readLocalLayoutSync();
    const hasLocal = !!localLayout;
    return {
      leftOrder: pinWelcomeHeaderFirst(
        hasLocal && Array.isArray(localLayout.left)
          ? mergeOrder(localLayout.left, DEFAULT_LEFT)
          : DEFAULT_LEFT.slice()
      ),
      rightOrder: hasLocal && Array.isArray(localLayout.right)
        ? mergeOrder(localLayout.right, DEFAULT_RIGHT)
        : DEFAULT_RIGHT.slice(),
      // 布局是否就绪：本地命中即视为就绪；无本地缓存时等接口返回再就绪
      // 未就绪时模板用骨架占位，避免新用户首次进入也出现重排闪烁
      layoutReady: hasLocal
    };
  },
  computed: {
    /**
     * 左侧模块映射
     */
    leftModuleMap() {
      return {
        welcomeHeader: WelcomeHeader,
        resourceZone: ResourceZone,
        opsMonitor: OpsMonitor,
        resourceMonitor: ResourceMonitor
      };
    },
    /**
     * 参与拖拽的左侧顺序（去掉固定置顶的 welcomeHeader）
     * 用作 draggable v-model：get 返回可拖拽列表，set 将变化写回 leftOrder（补回 welcomeHeader 在首位）
     */
    leftOrderDraggable: {
      get() {
        return this.leftOrder.filter(k => k !== 'welcomeHeader');
      },
      set(val) {
        this.leftOrder = pinWelcomeHeaderFirst(val);
      }
    },
    /**
     * 右侧模块映射，部分需要传入 props
     */
    rightModuleMap() {
      return {
        announcement: { comp: NoticeList, props: { noticeType: 'announcement' } },
        networkFreezeCutover: { comp: NoticeList, props: { noticeType: 'networkFreezeCutover' } },
        quickActions: { comp: QuickActions },
        workOrderList: { comp: WorkOrderList },
        customerService: { comp: CustomerService }
      };
    },
    /**
     * 当前用户标识，用于布局接口的 userId 字段
     * 兼容多种全局变量来源，缺失时使用 anonymous
     */
    currentUserId() {
      const g = this.GLOBAL || {};
      return g.userPin || g.uid || g.username || 'anonymous';
    },
    /**
     * 骨架占位块数量（保持与默认模块数量一致，撑起首屏高度）
     */
    leftSkeletonCount() {
      // WelcomeHeader 固定置顶始终可见，骨架只需占位其余模块
      return DEFAULT_LEFT.length - 1;
    },
    rightSkeletonCount() {
      return DEFAULT_RIGHT.length;
    }
  },
  mounted() {
    this.fetchLayout();
  },
  beforeDestroy() {
    // 防止极端情况下未触发 dragend，遗留 body 样式类
    this.toggleBodyDragging(false);
  },
  methods: {
    /**
     * 在 body 上切换 dragging class，用于全局禁止文本选中
     * （vuedraggable 拖拽中产生的 sortable-drag 元素挂在 body 上，
     *  scoped 样式无法覆盖，因此通过 body 全局类实现禁选）
     */
    toggleBodyDragging(active) {
      if (typeof document === 'undefined' || !document.body) return;
      const cls = 'overview-dragging';
      if (active) {
        document.body.classList.add(cls);
      } else {
        document.body.classList.remove(cls);
      }
    },

    /**
     * 拖拽开始
     */
    onDragStart() {
      this.toggleBodyDragging(true);
    },

    /**
     * 拖拽结束 - 关闭禁选 + 保存布局
     */
    onDragEnd() {
      this.toggleBodyDragging(false);
      this.onLayoutChange();
    },

    /**
     * 将布局缓存到 localStorage，作为接口异常时的降级数据源
     * 静默失败，不影响主流程
     * @param {Object} layout
     */
    writeLocalLayout(layout) {
      try {
        if (typeof window === 'undefined' || !window.localStorage) return;
        if (!isValidLayout(layout)) return;
        window.localStorage.setItem(
          LAYOUT_STORAGE_KEY,
          JSON.stringify(layout)
        );
      } catch (e) {
        // localStorage 写入失败（如隐私模式、配额超限）忽略
      }
    },

    /**
     * 将给定布局应用到 data（left/right）
     * 按字段独立处理：哪一侧合法就更新哪一侧，另一侧保持当前值
     * 仅在与当前顺序不同时才赋值，避免无意义触发响应式更新和 DOM 重排
     * @param {Object} layout
     * @returns {boolean} 是否实际发生了变更
     */
    applyLayout(layout) {
      if (!isValidLayout(layout)) return false;
      let changed = false;
      if (Array.isArray(layout.left)) {
        const next = pinWelcomeHeaderFirst(mergeOrder(layout.left, DEFAULT_LEFT));
        if (!isSameOrder(next, this.leftOrder)) {
          this.leftOrder = next;
          changed = true;
        }
      }
      if (Array.isArray(layout.right)) {
        const next = mergeOrder(layout.right, DEFAULT_RIGHT);
        if (!isSameOrder(next, this.rightOrder)) {
          this.rightOrder = next;
          changed = true;
        }
      }
      return changed;
    },

    /**
     * 获取布局并还原（三级降级）
     * 1) 接口 getOverviewLayout 返回合法 layout -> 使用接口数据，并刷新本地缓存
     * 2) 接口返回不合法 / 异常 -> 已在 data() 同步读取 localStorage（若有）
     * 3) localStorage 不存在或非法 -> 使用代码默认值（已在 data 初始化）
     */
    fetchLayout() {
      // 接口不可用：直接标记就绪（data() 已使用本地或默认顺序）
      if (!this.$api || typeof this.$api.getOverviewLayout !== 'function') {
        this.layoutReady = true;
        return;
      }
      const markReady = () => { this.layoutReady = true; };
      this.$api.getOverviewLayout({ userId: this.currentUserId }).then((res) => {
        const layout = (res && res.errno === 10000 && res.data && res.data.layout) || null;
        if (isValidLayout(layout)) {
          // 接口数据可用：与当前顺序比较，不一致才更新
          this.applyLayout(layout);
          // 同步刷新本地缓存（即便顺序未变，也以最新为准写回，保证下次首屏命中）
          this.writeLocalLayout({
            left: this.leftOrder.slice(),
            right: this.rightOrder.slice()
          });
        }
        // 接口数据缺失/非法：保留 data() 中的本地或默认顺序，无需额外处理
        markReady();
      }).catch(() => {
        // 接口异常：保留 data() 中的本地或默认顺序
        markReady();
      });
    },

    /**
     * 拖拽结束 -> 调用接口保存，并同步刷新本地缓存
     * 本地缓存独立于接口结果先行写入，确保下次进入页面时即便接口失败也有可用数据
     */
    onLayoutChange() {
      const layout = {
        left: pinWelcomeHeaderFirst(this.leftOrder.slice()),
        right: this.rightOrder.slice()
      };
      // 先写本地缓存（与接口请求互不依赖）
      this.writeLocalLayout(layout);
      if (!this.$api || typeof this.$api.saveOverviewLayout !== 'function') return;
      this.$api.saveOverviewLayout({
        userId: this.currentUserId,
        layout
      }).catch(() => {
        // 保存失败不打断用户操作
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.home-overview-container {
  display: flex;
  background-color: #f6fafd;
  height: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  margin-bottom: 16px;
  flex-direction: column;

  .main-container {
    padding: 16px;
    // 显式增加底部内边距，避免最末尾模块紧贴浏览器底部
    // padding-bottom: 24px;
    height: fit-content;
    display: flex;
    gap: 16px;
    // height: 100%;
    box-sizing: border-box;
  }

  .main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0; // 防止flex子项溢出
    // 左侧最末尾模块底部留白
    padding-bottom: 8px;
  }

  .side-content {
    width: clamp(300px, 25%, 460px);
    flex-shrink: 0;
    // 右侧最末尾模块底部留白
    padding-bottom: 8px;
  }
}

.jc-page-header {
  height: 45px !important;
  line-height: 45px !important;

  .drag-tip {
    margin-left: 12px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    font-weight: normal;
  }
}

// 拖拽样式
.draggable-block {
  margin-bottom: 16px;
  cursor: move;

  &:last-child {
    margin-bottom: 0;
  }

  // 固定置顶模块：不参与拖拽，不显示移动光标
  &--pinned {
    cursor: default;
  }
}

// 骨架占位：布局未就绪时使用，避免新用户首次进入页面出现重排闪烁
.layout-skeleton {
  display: flex;
  flex-direction: column;
}

.skeleton-block {
  margin-bottom: 16px;
  border-radius: 4px;
  background: linear-gradient(90deg, #eef3f8 25%, #f6fafd 37%, #eef3f8 63%);
  background-size: 400% 100%;
  animation: skeleton-loading 1.4s ease infinite;

  &:last-child {
    margin-bottom: 0;
  }

  &--left {
    height: 160px;

    &:first-child {
      // 与 WelcomeHeader 视觉高度对齐
      height: 96px;
    }
  }

  &--right {
    height: 180px;
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

// 拖拽过程中禁止文本被选中（仅在拖拽进行时生效，不影响平时复制）
.dragging-ghost,
.dragging-chosen,
.dragging-chosen *,
.sortable-drag-item,
.sortable-drag-item * {
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
}

.dragging-ghost {
  opacity: 0.4;
  background: #e6f1fc;
}

.dragging-chosen {
  box-shadow: 0 4px 14px rgba(29, 65, 255, 0.18);
}
</style>

<!-- 全局样式：拖拽过程中通过 body class 禁止整页文本选中
     （vuedraggable 在拖拽时会临时把克隆元素挂到 body 上，scoped 样式覆盖不到） -->
<style lang="scss">
body.overview-dragging,
body.overview-dragging * {
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  cursor: move !important;
}
</style>



// WEBPACK FOOTER //
// src/components/resourceOverview/HomeOverView.vue